import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  CircularProgress,
  Typography,
  Grid,
  Paper
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import EInvoiceService from '../../services/EInvoiceService';

const RecipientForm = () => {
  const { qrToken } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [existingRecipient, setExistingRecipient] = useState(null);
  
  const [formData, setFormData] = useState({
    recipientType: 'individual',
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    postalCode: '',
    city: '',
    state: '',
    country: 'Malaysia'
  });

  const malaysianStates = [
    'Johor', 'Kedah', 'Kelantan', 'Kuala Lumpur', 'Labuan', 'Malacca',
    'Negeri Sembilan', 'Pahang', 'Penang', 'Perak', 'Perlis', 'Putrajaya',
    'Sabah', 'Sarawak', 'Selangor', 'Terengganu'
  ];

  useEffect(() => {
    loadExistingRecipient();
  }, [qrToken]);

  const loadExistingRecipient = async () => {
    try {
      setLoading(true);
      const recipient = await EInvoiceService.getRecipientByQRToken(qrToken);
      
      if (recipient) {
        setExistingRecipient(recipient);
        setFormData({
          recipientType: recipient.recipientType || 'individual',
          companyName: recipient.companyName || '',
          firstName: recipient.firstName || '',
          lastName: recipient.lastName || '',
          email: recipient.email || '',
          phone: recipient.phone || '',
          address: recipient.address || '',
          postalCode: recipient.postalCode || '',
          city: recipient.city || '',
          state: recipient.state || '',
          country: recipient.country || 'Malaysia'
        });
      }
    } catch (err) {
      console.error('Error loading recipient:', err);
      setError('Invalid QR code or expired link. Please contact the clinic for assistance.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (formData.recipientType === 'company' && !formData.companyName.trim()) {
      return 'Company name is required for business recipients.';
    }
    
    if (formData.recipientType === 'individual') {
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        return 'First name and last name are required for individual recipients.';
      }
    }
    
    if (!formData.email.trim()) {
      return 'Email address is required.';
    }
    
    if (!formData.phone.trim()) {
      return 'Phone number is required.';
    }
    
    if (!formData.address.trim()) {
      return 'Address is required.';
    }
    
    if (!formData.city.trim()) {
      return 'City is required.';
    }
    
    if (!formData.state.trim()) {
      return 'State is required.';
    }
    
    if (!formData.postalCode.trim()) {
      return 'Postal code is required.';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return 'Please enter a valid email address.';
    }
    
    // Phone validation (Malaysian format)
    const phoneRegex = /^(\+?6?01[0-46-9]-*[0-9]{7,8}|(\+?603|06|05|04|07|08|09)-*[0-9]{7,8})$/;
    if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      return 'Please enter a valid Malaysian phone number.';
    }
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      await EInvoiceService.updateRecipientInfo(qrToken, formData);
      setSubmitted(true);
      
      // Redirect to success page after 3 seconds
      setTimeout(() => {
        navigate('/einvoice/success');
      }, 3000);
      
    } catch (err) {
      console.error('Error submitting recipient info:', err);
      setError('Failed to submit information. Please try again or contact the clinic.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          bgcolor: 'grey.50', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography color="text.secondary">Loading recipient form...</Typography>
        </Box>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          bgcolor: 'grey.50', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Information Submitted Successfully!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Thank you for providing your information. Your e-invoice will be generated and sent to your email address.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Redirecting to confirmation page...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 2 }}>
        <Card>
          <CardHeader 
            title={
              <Typography variant="h4" align="center" fontWeight="bold">
                E-Invoice Recipient Information
              </Typography>
            }
            subheader={
              <Typography align="center" color="text.secondary">
                Please provide your information to receive your e-invoice
              </Typography>
            }
          />
          
          <CardContent>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} sx={{ '& > *': { mb: 3 } }}>
              {/* Recipient Type */}
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'medium' }}>
                  Recipient Type
                </FormLabel>
                <RadioGroup
                  value={formData.recipientType}
                  onChange={(e) => handleInputChange('recipientType', e.target.value)}
                  row
                  sx={{ mt: 1 }}
                >
                  <FormControlLabel value="individual" control={<Radio />} label="Individual" />
                  <FormControlLabel value="company" control={<Radio />} label="Company/Business" />
                </RadioGroup>
              </FormControl>

              {/* Company Name (if business) */}
              {formData.recipientType === 'company' && (
                <TextField
                  fullWidth
                  label="Company Name *"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  placeholder="Enter company name"
                />
              )}

              {/* Individual Names (if individual) */}
              {formData.recipientType === 'individual' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="First Name *"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      placeholder="Enter first name"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Last Name *"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      placeholder="Enter last name"
                    />
                  </Grid>
                </Grid>
              )}

              {/* Contact Information */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address *"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number *"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="e.g., 012-3456789"
                  />
                </Grid>
              </Grid>

              {/* Address */}
              <TextField
                fullWidth
                label="Address *"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Enter full address"
              />

              {/* City, State, Postal Code */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City *"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="Enter city"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>State *</InputLabel>
                    <Select
                      value={formData.state}
                      label="State *"
                      onChange={(e) => handleInputChange('state', e.target.value)}
                    >
                      {malaysianStates.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Postal Code *"
                    value={formData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    placeholder="e.g., 50450"
                  />
                </Grid>
              </Grid>

              {/* Country */}
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                InputProps={{
                  readOnly: true,
                }}
              />

              {/* Submit Button */}
              <Box sx={{ pt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : null}
                >
                  {submitting ? 'Submitting Information...' : 'Submit Information'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default RecipientForm;